import { useEffect, useRef, type ReactNode, type Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { PlacesAutocompleteInput } from './PlacesAutocompleteInput';
import { DeliveryQuotePanel, type QuoteStatus } from './DeliveryQuotePanel';
import type { Destination } from './deliveryApi';
import type { DeliveryAddress } from '@/types/order';
import type { UserAddress } from '@/types/account';

interface Props {
  address: DeliveryAddress;
  onAddressChange: (a: DeliveryAddress) => void;
  quote: QuoteStatus;
  triggerQuote: (destination: Destination) => void;
  onRetry?: () => void;
  savedAddresses?: UserAddress[];
  onPickSaved?: (a: UserAddress) => void;
  onRemoveSaved?: (id: string) => void;
  servedCities?: string[];
  panelRef?: Ref<HTMLDivElement>;
  /** SC-1205 — ce que le devis entraîne (minimum de la zone non atteint), affiché sous lui. */
  quoteNotice?: ReactNode;
}

const DEBOUNCE_MS = 300;

export function DeliveryStep({
  address,
  onAddressChange,
  quote,
  triggerQuote,
  onRetry,
  savedAddresses = [],
  onPickSaved,
  onRemoveSaved,
  servedCities = [],
  panelRef,
  quoteNotice,
}: Props) {
  const { t } = useTranslation();
  const timerRef = useRef<number | null>(null);
  const usableSaved = savedAddresses
    .filter((a) => a.place_id)
    .slice()
    .sort((x, y) => Number(Boolean(y.isDefault)) - Number(Boolean(x.isDefault)));

  function handlePick(parts: {
    place_id: string;
    lat: number;
    lng: number;
    formatted_address: string;
    street: string;
    city: string;
    zip: string;
  }) {
    const next: DeliveryAddress = { ...address, ...parts };
    onAddressChange(next);
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(
      () =>
        triggerQuote({
          place_id: parts.place_id,
          lat: parts.lat,
          lng: parts.lng,
          formatted_address: parts.formatted_address,
        }),
      DEBOUNCE_MS,
    );
  }

  function handleInstructions(text: string) {
    onAddressChange({ ...address, instructions: text });
  }

  useEffect(
    () => () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    },
    [],
  );

  return (
    <section className="space-y-3">
      <h3 className="font-display text-xl mb-3">{t('checkout.delivery.title')}</h3>
      {servedCities.length > 0 && (
        <p data-testid="served-cities" className="text-sm">
          {t('checkout.delivery.servedCities', { cities: servedCities.join(', ') })}
        </p>
      )}
      {usableSaved.length > 0 && onPickSaved && (
        <fieldset
          className="rounded border-2 border-ink p-3 space-y-2"
          data-testid="saved-addresses"
        >
          <legend className="text-sm font-medium px-1">
            {t('checkout.delivery.savedAddresses')}
          </legend>
          {usableSaved.map((a) => {
            const active = address.place_id === a.place_id;
            return (
              <div key={a.id} className="flex items-stretch gap-1">
                <button
                  type="button"
                  onClick={() => {
                    // SC-1155 : une adresse saisie puis, dans les 300 ms, une
                    // adresse enregistrée — le devis différé de la première
                    // partait APRÈS celui de la seconde et restait affiché :
                    // frais et minimum d'une zone, adresse d'une autre.
                    if (timerRef.current) window.clearTimeout(timerRef.current);
                    onPickSaved(a);
                  }}
                  aria-pressed={active}
                  data-testid={`saved-address-${a.id}`}
                  className={`flex-1 text-left px-3 py-2 border ${
                    active ? 'border-ink bg-yellow-50' : 'border-gray-300'
                  }`}
                >
                  <span className="font-medium">{a.label}</span>
                  {a.isDefault && (
                    <span
                      className="ml-2 inline-block text-xs px-2 py-0.5 bg-mint border border-ink rounded-btn-sm align-middle"
                      data-testid={`saved-address-default-badge-${a.id}`}
                    >
                      {t('checkout.delivery.default')}
                    </span>
                  )}
                  {a.formatted_address && a.formatted_address !== a.label && (
                    <span className="block text-xs text-gray-600">
                      {a.formatted_address}
                    </span>
                  )}
                </button>
                {onRemoveSaved && (
                  <button
                    type="button"
                    onClick={() => {
                      const msg = t('checkout.delivery.removeAddressConfirm', { label: a.label });
                      if (window.confirm(msg)) onRemoveSaved(a.id);
                    }}
                    aria-label={t('checkout.delivery.removeAddress')}
                    title={t('checkout.delivery.removeAddress')}
                    data-testid={`remove-saved-address-${a.id}`}
                    className="px-3 border border-gray-300 text-gray-600 hover:bg-red-50 hover:text-red-700"
                  >
                    ×
                  </button>
                )}
              </div>
            );
          })}
        </fieldset>
      )}
      <PlacesAutocompleteInput
        initialValue={address.formatted_address ?? ''}
        onPick={handlePick}
      />
      {address.formatted_address && (
        <div data-testid="address-resolved">📍 {address.formatted_address}</div>
      )}
      <label className="block">
        <span className="text-sm">{t('checkout.delivery.instructions')}</span>
        <textarea
          value={address.instructions ?? ''}
          onChange={(e) => handleInstructions(e.target.value)}
          rows={2}
          className="w-full border-2 border-ink p-2 font-mono"
          data-testid="instructions"
        />
      </label>
      {quote.status !== 'idle' && (
        <div ref={panelRef} className="space-y-3">
          <DeliveryQuotePanel quote={quote} onRetry={onRetry} />
          {quoteNotice}
        </div>
      )}
    </section>
  );
}
